import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Restapi from '@utils/api';
import Skeleton from '@components/ui_skeleton/component';
import Text from '@text';
import { t } from '@utils/i18n';
import { useApp } from '../../../ssr/shared/application-context';
import GiftcardAction from './giftcard';
import PhysicalGiftcard from './physicalGiftcard';
import AddToCartButton from '@components/ui_add-to-cart-button/component';
import DigitalProductCTA from './digitalProduct';
import ChooseStoreButton from '@components/choose-store-button/component';
import { Button, Icon } from 'audamatic-ui';
import { checkOpeningAndClosingHoursShop } from '@utils';
import KeepMePosted from '../_keep-me-posted/component';

import './styles.scss';

const PrimaryActionsB2C = (props) => {
  const { product, store } = props;
  const { isDigitalProduct, isGiftcard, deliveryDays, cutOffTime, availability, SKU, offered, fullProductAvailability } = product;

  // Render gifcard form if it is a giftcard.
  if (isGiftcard && isDigitalProduct) {
    return (
        <GiftcardAction store={store} product={product} />
      );
    }

  // Render digital products primary actions when it is a digital product
  // Currently only for product types eBook and Digitale Cadeaukaarten
  if (isDigitalProduct) {
    return <DigitalProductCTA store={store} product={product} />
  }

  const restapi = new Restapi();
  const appCtx = useApp();
  const loggedout = !appCtx.customerId;
  const { preferredStoreId } = appCtx;

  const [deliverydaysInfo, setDeliverydaysInfo] = useState({});
  const [prefStoreInfo, setPrefStoreInfo] = useState({});
  const [stockData, setStockData] = useState({});
  const [prefStoreHasStock, setPrefStoreHasStock] = useState(null);
  const [loadingHomeDelivery, setLoadingHomeDelivery] = useState(true);
  const [loadingClickCollect, setLoadingClickCollect] = useState(true);
  const [selectedStoreId, setSelectedStoreId] = useState(preferredStoreId);
  const [newAmount, setNewAmount] = useState(null);

  useEffect(() => {
    //Fetching the product delivery days info that is not cached
    const currentDate = new Date().toISOString().split('T')[0].replaceAll('-', '');
    fetchProductDeliveryDays(currentDate);
  }, [])

  useEffect(() => {
    if (loggedout) {
      const sessionStoreId = sessionStorage.getItem('selectedStoreId');
      if (!selectedStoreId && sessionStoreId) {
        setSelectedStoreId(sessionStoreId);
      } else if (selectedStoreId && sessionStoreId !== selectedStoreId) {
        sessionStorage.setItem('selectedStoreId', selectedStoreId);
      }
    } else {
      if (selectedStoreId !== preferredStoreId) {
        setSelectedStoreId(preferredStoreId);
      }
    }
  }, [loggedout, selectedStoreId, preferredStoreId]);

  useEffect(() => {
    if (selectedStoreId) {
      if (product?.stockCheck) {
        fetchPrefStoreInfo();
      } else {
        setLoadingClickCollect(false)
      }
    } else {
      setLoadingClickCollect(false)
    }
  }, [selectedStoreId])

  const fetchProductDeliveryDays = (selectedDate) => {
    restapi.getProductDeliverydaysNotCached(selectedDate)
      .then(data => {
        setDeliverydaysInfo(data)
      })
      .finally(() => {
        setLoadingHomeDelivery(false);
      });
  }

  // Fetching stock data for this product
  // Filter out the stock data from the pref store and set the stock data with the info
  // If pref store has stock set the state to true, otherwise false
  const fetchStockData = (valkStoreID) => {
    restapi.getStockInfo(SKU)
      .then(response => {
        const { stockInfo } = response
        const prefStoreStock = stockInfo.find(entry => entry.store.branchNumber === parseInt(valkStoreID, 10));
        setStockData({ storeId: prefStoreStock?.store.id, productId: response.id });
        if (prefStoreStock?.availableQuantity) {
          setPrefStoreHasStock(true);
        } else {
          setPrefStoreHasStock(false);
        }
      })
      .finally(() => {
        setLoadingClickCollect(false);
      });
  }

  // Fetch the info from the preferred store
  // If the stockcheck attribute on the product is stockCheck and that value is true for the store
  // Or the stockCheck attribute is stockCheckQuantore and that value is true for the store
  // Start fetching stock data
  const fetchPrefStoreInfo = () => {
    restapi.getSingleStore(selectedStoreId)
      .then(response => {
        setPrefStoreInfo(response);
        const {
          valkStoreID,
          stockCheck,
          stockCheckQuantore } = response;

        if ((product.stockCheck === 'stockCheck' && !!stockCheck) || (product.stockCheck === 'stockCheckQuantore' && !!stockCheckQuantore)) {
          fetchStockData(valkStoreID);
        } else {
          setPrefStoreHasStock(false);
          setLoadingClickCollect(false);
        }
      })
  }

  // Import the store component on click, so it's not inlcuded when there is no preferred store with stock
  const openCheckStockInStores = () => {
    import('../../sec_stock-check/_store-checker/component')
      .then((component) => {
        const StoreChecker = component.default;
        store.modal.openModal('popup', '', {
          title: t('stock.title'),
          component: <StoreChecker product={product} store={store} newAmount={newAmount}/>,
          classNames: 'stock-check',
        });
      });
  };

  const openClickAndCollectProduct = () => {
    import('../../sec_stock-check/_store-checker/component')
      .then((component) => {
        const StoreChecker = component.default;
        store.modal.openModal('popup', '', {
          title: t('stock.title'),
          component: <StoreChecker product={product} store={store} storeAssigned={prefStoreInfo} stockData={stockData} newAmount={newAmount} />,
        });
      });
  };

  const checkIfStoreIsOpen = () => {
    const { attributes } = prefStoreInfo;
    if (!attributes) {
      return
    }

    const openingHours = checkOpeningAndClosingHoursShop(attributes);
    const { closingTime, openingTime, deviantOpeningHours } = openingHours;

    // When the store is closed and there is no closingTime or openingTime
    // Return false
    if (!closingTime || !openingTime) {
      return false
    } else if (deviantOpeningHours) {
      return 'deviantOpeningHours'
    }

    const { currentTime } = deliverydaysInfo;
    const [closingHour, closingMinute] = closingTime?.split(/\.|:/)

    //Creating a date object with the closing hours as time
    let today = new Date();
    today.setHours(closingHour, closingMinute, 0, 0)
    //Subtracting 1 hour 15min from the closing hours
    today.setMinutes(today.getMinutes() - 75);

    const closingHourWithBuffer = `${today.getHours().toString().padStart(2, '0')}${today.getMinutes().toString().padStart(2, '0')}`
    const openingHourWithoutSeperator = openingTime.split(/\.|:/).join('');

    if (currentTime < Number(openingHourWithoutSeperator)) {
      return 'opensLaterToday'
    }

    const isOpen = currentTime >= Number(openingHourWithoutSeperator) && currentTime <= Number(closingHourWithBuffer)
    return isOpen
  }

  const calculatePickupTime = (currentTime) => {
    const currentTimeString = currentTime.toString();
    const hour = currentTimeString.length < 4 ? currentTimeString.slice(0, 1) : currentTimeString.slice(0, 2);
    const minute = currentTimeString.slice(-2);

    // Creating a date object with the current time as time
    // And adding 1 hour
    let today = new Date();
    today.setHours(hour, minute, 0, 0)
    today.setHours(today.getHours() + 1);

    //Round the minutes off to the first quarter of an hour
    let roundedOffMinutes = Math.ceil(today.getMinutes() / 15) * 15;
    if (roundedOffMinutes === 60) {
      today.setHours(today.getHours() + 1);
      roundedOffMinutes = 0;
    }

    //Use the rounded off minutes on the currentTime + 1 hour
    today.setMinutes(roundedOffMinutes);

    const pickupTime = `${today.getHours().toString().padStart(2, '0')}:${today.getMinutes().toString().padStart(2, '0')}`
    return pickupTime;
  }

  // Offered 1: available
  // Offered 5: Out of stock, keep me posted
  const renderHomeDelivery = () => {
    const {
      currentTime,
      firstAvailDelivDayBeforeCutOff,
      firstAvailDelivDayAfterCutOff,
    } = deliverydaysInfo;
    const beforeCutOff = Number(currentTime) < cutOffTime;
    const afterCutOff = Number(currentTime) > cutOffTime;
    const deliveringTomorrow = beforeCutOff && firstAvailDelivDayBeforeCutOff === 1
    const deliveringOvermorrow = beforeCutOff && firstAvailDelivDayBeforeCutOff === 2 || afterCutOff && firstAvailDelivDayAfterCutOff === 2
    const availabilityText = offered === 1 ? availability : fullProductAvailability

    let title = <Text>home.delivery.moreThanOneDay.title</Text>
    let description = <Text replacements={{ availability: availabilityText }}>home.delivery.moreThanOneDay.description</Text>

    if (offered === 1 && deliveryDays === 1 && (deliveringTomorrow || deliveringOvermorrow)) {
      if (beforeCutOff && deliveringTomorrow) {
        title = <Text>home.delivery.oneDay.before.cutoff.title</Text>
      } else if ((beforeCutOff && deliveringOvermorrow) || afterCutOff) {
        title = <Text>home.delivery.oneDay.after.cutoff.title</Text>
      }

      description = <Text>home.delivery.oneDay.description</Text>
    }

    return (
      <div className='homeDelivery'>
        <div className='info'>
          <span className='title-homeDelivery'>
            {title}
          </span>
          <span className='description-homeDelivery'>
            {description}
          </span>
        </div>
        <div className="button-wrapper">
          {offered === 5 ? (
            <KeepMePosted product={product} store={store} />
          ) : (
            <AddToCartButton
              product={product}
              store={store}
              dataTestId="button-addToCart"
              showQuantity={false}
              showOrderlineReference={true}
              showExpressOrder={true}
            />
          )}
        </div>
      </div>
    )
  }

  const renderClickAndCollect = () => {
    const storeIsOpen = checkIfStoreIsOpen();
    const { isPublicHoliday, currentTime } = deliverydaysInfo;

    let title = <Text>clickcollect.delivery.noStoreOrStock.title</Text>;
    let descriptionText = <Text>clickcollect.delivery.noStoreOrStock.text</Text>;
    let buttonOnClick = openCheckStockInStores;
    let buttonText = <Text>clickcollect.delivery.noStoreOrStock.button</Text>;

    if (selectedStoreId && prefStoreHasStock) {
      buttonOnClick = openClickAndCollectProduct;
      buttonText = <Text>clickcollect.delivery.button</Text>;

      if (isPublicHoliday || storeIsOpen === 'deviantOpeningHours') {
        title = <Text>clickcollect.delivery.closed.title</Text>
        descriptionText = <Text>clickcollect.delivery.publicHoliday.text</Text>
      } else if (storeIsOpen === 'opensLaterToday') {
        const { attributes } = prefStoreInfo;
        const openingHours = checkOpeningAndClosingHoursShop(attributes);
        const { openingTime } = openingHours;

        const pickupTime = calculatePickupTime(openingTime)
        title = <Text>clickcollect.delivery.open.title</Text>
        descriptionText = <Text replacements={{ time: pickupTime }}>clickcollect.delivery.open.text</Text>
      } else if (storeIsOpen) {
        const pickupTime = calculatePickupTime(currentTime);
        title = <Text>clickcollect.delivery.open.title</Text>
        descriptionText = <Text replacements={{ time: pickupTime }}>clickcollect.delivery.open.text</Text>
      } else {
        title = <Text>clickcollect.delivery.closed.title</Text>
        descriptionText = <Text>clickcollect.delivery.closed.text</Text>
      }
    } else if (isPublicHoliday || storeIsOpen === 'deviantOpeningHours') {
      descriptionText = <Text>clickcollect.delivery.publicHoliday.text</Text>
    }

    return (
      <div className='clickAndCollect'>
        <div className='info'>
          <span className='title-clickAndCollect'>
            {title}
          </span>
          <div className='description-clickAndCollect'>
            <span className='text-clickAndCollect'>
              {descriptionText}
            </span>
            <span className='usp-clickAndCollect'>
              <Text>clickcollect.delivery.usp</Text>
            </span>
          </div>
          {selectedStoreId && prefStoreHasStock ? (
            <div className="prefStore-info">
              <span className="prefStore-address">{`${prefStoreInfo.city} - ${prefStoreInfo.address}`}</span>
              <ChooseStoreButton variant="link" icon="MapPin" />
            </div>
          ) : null}
        </div>
        <div className="button-wrapper">
          <Button
            variant="tertiary"
            size="large"
            iconLeft={<Icon name="MapPin" size="sm" />}
            fullWidth={true}
            onClick={buttonOnClick}
            data-test-id="button-clickAndCollect"
          >
            {buttonText}
          </Button>
        </div>
      </div>
    )
  }

  // Offerred 4: Only available in store, not online
  if (offered === 4) {
    return (
      <div className='pdp-primaryActions b2c offered-other'>
          {isGiftcard && (
            <PhysicalGiftcard product={product} store={store} setNewAmount={setNewAmount} />
          )}
        <div className='productDetailButtons' data-test-id="comp-pdpCtaButtons">
          <div className='wrapper-primaryActions'>
            {loadingClickCollect ? <Skeleton height={'125px'} /> : renderClickAndCollect()}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='pdp-primaryActions b2c offered-other'>
      <div className='productDetailButtons' data-test-id="comp-pdpCtaButtons">
        <div className='wrapper-primaryActions'>
          {loadingHomeDelivery ? <Skeleton height={'125px'} /> : renderHomeDelivery()}
          {product.stockCheck && (
            <>
              <span className='divider' />
              {loadingClickCollect ? <Skeleton height={'125px'} /> : renderClickAndCollect()}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

PrimaryActionsB2C.propTypes = {
  product: PropTypes.object,
  store: PropTypes.object,
};

export default PrimaryActionsB2C;